/**
 * ProductCard - Vignette produit cliquable
 * Clic sur la carte = ouvre la modal de détails avec bouton "Ajouter au panier"
 * Style « marché artisanal » : carte blanche, prix terracotta.
 * Pas de badge bio par produit (le bandeau header affirme le 100% bio).
 * Côté stock : on n'affiche NI la quantité, NI « il en reste N » (décision de
 * Philippe, 01/09/2026 — le stock restant ne regarde pas le client ; s'il en
 * commande plus que disponible, il l'apprend à la commande via
 * check-availability). Seule la rupture reste, en filet : elle bloque l'ajout
 * d'un article tombé à 0 pendant la consultation. Cf. utils/shopStock.ts.
 */

import type { Product } from '../../../types/ecommerce.types';
import { adresseProduit } from '../../../utils/adresseProduit';
import { useTenant } from '../../../contexts/TenantContext';
import { getShopStockState } from '../../../utils/shopStock';
import { useShopOrdering } from '../../../hooks/ecommerce/useShopOrdering';
import { isOrderableNow, formatJourReouverture } from '../../../utils/shopOrdering';
import PastilleVegan from './PastilleVegan';

interface ProductCardProps {
  product: Product;
  onInfoClick?: () => void;
  /** Favoris : pour tous ; sans compte, la liste reste dans le navigateur (voir useFavoris). */
  estFavori?: boolean;
  onBasculerFavori?: () => void;
}

export default function ProductCard({ product, onInfoClick, estFavori, onBasculerFavori }: ProductCardProps) {
  const { tenant } = useTenant();
  const ordering = useShopOrdering();
  const showVeganBadgeOnCards = tenant?.config?.ecommerce?.showVeganBadgeOnCards ?? false;

  // Récupérer le prix EUR
  const priceEur = product.prices?.find((p) => p.currency === 'EUR');
  const priceDisplay = priceEur ? (priceEur.amount_cents / 100).toFixed(2) : '-.--';

  // Le nom est un VRAI lien vers la page du produit (18/10/2026) : Google suit
  // les liens, il ne clique pas sur les cartes. Un clic simple garde le geste
  // habituel (la fiche s'ouvre en fenêtre, via le clic de la carte) ; un clic
  // « nouvel onglet » (Cmd/Ctrl, molette) ouvre la page du produit.
  const adresse = adresseProduit(product);
  const surClicNom = (e: React.MouseEvent) => {
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) {
      e.stopPropagation();
      return;
    }
    e.preventDefault();
  };

  // Clic sur la carte = ouvrir la modal de détails
  const handleCardClick = () => {
    if (onInfoClick) {
      onInfoClick();
    }
  };

  // Rupture : bloque l'ajout au panier (une précommande n'est jamais en rupture,
  // elle se commande pour une date de fabrication).
  const stockState = getShopStockState(product);
  const isOutOfStock = stockState === 'out';
  // Hors fenêtre de commande, le frais n'est pas commandable — l'épicerie et
  // les pains sur commande, si. La vignette reste consultable dans les deux cas.
  const commandable = isOrderableNow(product, ordering);

  return (
    <div
      onClick={isOutOfStock ? undefined : handleCardClick}
      className={`shop-sans group relative bg-white border border-black/[.08] rounded-[10px] overflow-hidden flex flex-col transition-all duration-200 ${
        isOutOfStock
          ? 'opacity-60 cursor-not-allowed'
          : 'cursor-pointer hover:shadow-md hover:border-[#b45f2e]/40 active:scale-[0.98]'
      }`}
    >
      {/* Badge Précommande en haut à gauche */}
      {product.preorder_enabled && (
        <div className="absolute top-1.5 left-1.5 z-10 px-2 py-0.5 text-[11px] font-bold bg-[#4a5c3a] text-white rounded-full shadow-sm flex items-center gap-1">
          <span>📅</span>
          <span>Préco</span>
        </div>
      )}

      {/* Badge Végan (si pas de précommande, activé par config tenant) */}
      {!product.preorder_enabled && showVeganBadgeOnCards && product.vegan && (
        <div className="absolute top-1.5 left-1.5 z-10">
          <PastilleVegan />
        </div>
      )}

      {/* Rupture — indispensable : la carte n'est plus cliquable */}
      {isOutOfStock && (
        <div className="absolute top-1.5 right-1.5 z-10 px-2 py-0.5 text-[11px] font-bold bg-[#6b6550] text-white rounded-full shadow-sm">
          Rupture
        </div>
      )}

      {/* Pas commandable aujourd'hui (frais hors fenêtre) */}
      {!commandable && !isOutOfStock && (
        // Crème, pas vert plein : c'est une DATE, pas une interdiction. Le vert
        // foncé pleine pastille se lisait comme un refus et donnait au rayon
        // frais l'air d'être fermé pour de bon (retours clients, 09/09/2026).
        <div className="absolute top-1.5 right-1.5 z-10 px-2 py-0.5 text-[11px] font-bold bg-[#f0e9d8] text-[#4a4530] border border-[#4a5c3a]/25 rounded-full shadow-sm">
          {formatJourReouverture(ordering?.next_open_at)}
        </div>
      )}


      {/* Image Container */}
      <div className={`relative w-full h-[110px] bg-[#f0e9d8] overflow-hidden ${isOutOfStock ? 'opacity-50' : ''}`}>
        {product.image_url ? (
          <img
            src={product.thumbnail_url || product.image_url}
            alt={product.name}
            /* Le catalogue tient une centaine de vignettes : sans chargement
               paresseux, chacune télécharge sa photo même à mille pixels sous
               l'écran. Mesuré le 25/08 : 40 vignettes = 6,3 Mo en image pleine,
               contre ~15 Ko par miniature, et seulement celles qu'on voit. */
            loading="lazy"
            decoding="async"
            /* object-contain : la photo entière, jamais tronquée — les photos
               sont cadrées à la main (revue 09/2026), c'est ce cadre qui doit
               s'afficher. Le fond blanc remplit les marges éventuelles. */
            className="w-full h-full object-contain bg-white group-hover:scale-105 transition-transform duration-200"
          />
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center px-2">
            <a href={adresse} onClick={surClicNom} className="text-sm font-bold text-[#4a4530] text-center line-clamp-3 leading-tight">
              {product.name}
            </a>
          </div>
        )}

        {/* Notre production : le logo posé sur la photo, comme un cachet de
            producteur. En pastille de texte en haut de carte, il chevauchait le
            badge de réouverture (« mercredi ») dès que le nom était long — et
            les quatre coins de la vignette sont déjà pris par le stock et les
            précommandes. Ici il ne dispute la place à personne. Bas-DROIT
            depuis le 01/11/2026 (demande de Philippe), le cœur prend le
            bas-gauche. */}
        {product.from_own_production && (
          <div
            className="absolute bottom-1 right-1 w-9 h-9 rounded-full bg-white/95 ring-1 ring-black/10 shadow-sm flex items-center justify-center overflow-hidden"
            title="Notre production — cultivé à Elouges"
          >
            <img
              src="/logo-folies-badge.png"
              alt="Notre production"
              className="w-full h-full object-contain p-[3px]"
              loading="lazy"
            />
          </div>
        )}
        {/* Favori : coin BAS-GAUCHE — les deux du haut portent precommande,
            vegan, rupture ou jour de reouverture, et le logo « Notre
            production » occupe le bas-droit depuis le 01/11/2026.
            `stopPropagation` : la carte entiere ouvre la fiche, et mettre en
            favori ne doit pas l'ouvrir. */}
        {onBasculerFavori && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              onBasculerFavori();
            }}
            aria-label={estFavori ? 'Retirer des favoris' : 'Ajouter aux favoris'}
            aria-pressed={!!estFavori}
            title={estFavori ? 'Retirer des favoris' : 'Ajouter aux favoris'}
            className="absolute bottom-1 left-1 w-9 h-9 rounded-full bg-white/95 ring-1 ring-black/10 shadow-sm flex items-center justify-center active:scale-95 transition-transform"
          >
            <span className={`text-lg leading-none ${estFavori ? '' : 'opacity-35 grayscale'}`}>
              {estFavori ? '❤️' : '🤍'}
            </span>
          </button>
        )}
      </div>

      {/* Info Container */}
      <div className="p-[11px] flex flex-col flex-1">
        {/* Nouveauté : le mot dans le bloc TEXTE, jamais un badge de coin. Les
            quatre coins sont pris (précommande, végan, rupture ou jour de
            réouverture, logo « Notre production ») et c'est ce chevauchement
            qui avait fait déplacer le logo sur la photo le 25/08/2026. */}
        {product.is_novelty && (
          <span className="self-start mb-1 px-1.5 py-[1px] rounded bg-[#e3ede6] text-[#2d6a4f] text-[10px] font-bold uppercase tracking-wide">
            {product.novelty_word || 'Nouveau'}
          </span>
        )}

        {/* Nom produit (seulement si image présente) */}
        {product.image_url && (
          <h3 className="font-bold text-[#3a3324] text-[12.5px] leading-[1.3] mb-1 line-clamp-2 min-h-[32px]">
            <a href={adresse} onClick={surClicNom}>{product.name}</a>
          </h3>
        )}

        {/* Prix — promo DLC courte : prix barré + remise + DLC lisible (le client
            doit savoir POURQUOI c'est moins cher et jusqu'à quand ça se mange) */}
        <div className="mt-auto">
          {product.promo_percent && product.promo_original_price_cents ? (
            <>
              <div className="flex items-baseline gap-1.5 flex-wrap">
                <span className="text-[11px] text-[#6b6550] line-through">
                  {(product.promo_original_price_cents / 100).toFixed(2)} €
                </span>
                <span className="font-bold text-[13.5px] leading-none text-[#c02626]">
                  {priceDisplay} €
                  {product.unit && (
                    <span className="font-normal text-[11px]">
                      {product.is_weighted ? `/${product.unit}` : '/unité'}
                    </span>
                  )}
                </span>
                <span className="text-[10px] font-bold text-white bg-[#c02626] rounded px-1 py-[1px]">
                  −{product.promo_percent} %
                </span>
              </div>
              {product.display_expiry_date && (
                <div className="text-[10.5px] text-[#c02626] mt-0.5">
                  DLC {new Date(product.display_expiry_date).toLocaleDateString('fr-BE')}
                </div>
              )}
            </>
          ) : (
            <div className="font-bold text-[13.5px] leading-none text-[#b45f2e]">
              {priceDisplay} €{' '}
              {product.unit && (
                <span className="font-normal text-[11px] text-[#6b6550]">
                  {product.is_weighted ? `/${product.unit}` : '/unité'}
                </span>
              )}
            </div>
          )}
          {/* Offre anti-gaspi d'un LOT : le produit reste au prix normal, un lot
              daté plus court est proposé moins cher (choix sur la fiche). */}
          {!product.promo_percent && product.lot_promos && product.lot_promos.length > 0 && (() => {
            const meilleure = [...product.lot_promos].sort((a, b) => b.percent - a.percent)[0];
            return (
              <div
                className="mt-1 inline-flex max-w-full items-center gap-1 whitespace-nowrap text-[10.5px] font-bold text-white bg-[#c02626] rounded px-1.5 py-[2px]"
                title={`Anti-gaspi −${meilleure.percent} % : ${(meilleure.price_cents / 100).toFixed(2)} €`}
              >
                {/* Court, sur UNE ligne : dans une vignette étroite, « Anti-gaspi −20 % ·
                    3.12 € » se cassait sur quatre lignes (capture iPad du 27/09). */}
                <span aria-hidden>🏷️</span>
                <span className="sr-only">Anti-gaspi</span>−{meilleure.percent} % · {(meilleure.price_cents / 100).toFixed(2)} €
              </div>
            );
          })()}
        </div>
      </div>
    </div>
  );
}
